import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProject from '../../../hooks/useProject';
import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from '@reach/accordion';
import '@reach/accordion/styles.css';
import './ProducerControlPanel.scss';
import ExpandCollapseIcon from '../../../images/back-arrow-blue.png';
import StreamSourcePanel from './Panels/StreamSourcePanel/StreamSourcePanel';
import StreamDestinationsPanel from './Panels/StreamDestinationsPanel/StreamDestinationsPanel';
import CaptioningPanel from './Panels/CaptioningPanel/CaptioningPanel';
import Chat from '../../Chat/Chat';
import { getDateDifferenceFromNow } from '../../../helpers/dateFormatHelper/dateFormatHelper';
import moment from 'moment';
import { Project } from '../../../types/Project';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import AggregatorPanel from './Panels/AggregatorPanel/AggregatorPanel';
import OverlayPanel from './Panels/OverlayPanel/OverlayPanel';
import TranscoderPanel from './Panels/TranscoderPanel/TranscoderPanel';
import { Messages } from '../../../types/Message';
import GlossariesPanel from './Panels/GlossariesPanel/GlossariesPanel';
import ConnectorPanel from './Panels/ConnectorPanel/ConnectorPanel';

type ProducerControlPanelProps = {
  chatMessages: Messages;
  sendMessageToRoomCallback: (messageToSend: string) => void;
  isChatConnected: boolean;
  sendProjectToAllUsers: (project: Project) => void;
};

const ProducerControlPanel = ({
  chatMessages,
  sendMessageToRoomCallback,
  isChatConnected,
  sendProjectToAllUsers,
}: ProducerControlPanelProps) => {
  const { t } = useTranslation();
  const { updateStream, project } = useProject();
  const [captionMode, setCaptionMode] = useState(project.encoder.captionMode);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [, setIsProjectStarted] = useState<boolean>(false);
  const [, setIsProjectStartDatePassed] = useState<boolean>(false);
  const [destinationDots, setDestinationDots] = useState<object>();
  const [glossaryDots, setGlossaryDots] = useState<object>();

  const createDestinationDots = useCallback(() => {
    if (!project || !project.encoder) {
      return null;
    }
    const destinationDotsCount = 10;
    const destinationDots = [];
    const count =
      project.stream?.encoders?.reduce(
        (sum, encoder) => sum + (encoder.destinations?.length || 0),
        0,
      ) ?? 0;

    if (count >= destinationDotsCount) {
      for (let x = 0; x < destinationDotsCount; x++) {
        destinationDots.push(<span key={x} className="control-panel-multi-dot"></span>);
      }
    } else {
      for (let x = 0; x < count; x++) {
        destinationDots.push(<span key={x} className="control-panel-multi-dot"></span>);
      }
      for (let y = 0; y < destinationDotsCount - count; y++) {
        destinationDots.push(
          <span
            key={y + destinationDotsCount}
            className="control-panel-multi-dot control-panel-multi-dot-grey"
          ></span>,
        );
      }
    }

    return setDestinationDots(destinationDots);
  }, [project, setDestinationDots]);

  const createGlossaryDots = useCallback(() => {
    if (!project) {
      return null;
    }

    const glossaryDotsCount = 10;
    const glossaryDots = [];
    const count =
      project.stream?.glossaries?.length ?? 0;

    if (count >= glossaryDotsCount) {
      for (let x = 0; x < glossaryDotsCount; x++) {
        glossaryDots.push(<span key={x} className="control-panel-multi-dot"></span>);
      }
    } else {
      for (let x = 0; x < count; x++) {
        glossaryDots.push(<span key={x} className="control-panel-multi-dot"></span>);
      }
      for (let y = 0; y < glossaryDotsCount - count; y++) {
        glossaryDots.push(
          <span
            key={y + glossaryDotsCount}
            className="control-panel-multi-dot control-panel-multi-dot-grey"
          ></span>,
        );
      }
    }

    return setGlossaryDots(glossaryDots);
  }, [project, setGlossaryDots]);

  useEffect(() => {
    const isProjectBroadcastFinished = () => {
      if ((project?.broadcastStartedUtc && project?.broadcastEndedUtc) || project?.deletedUtc) {
        setIsReadOnly(true);
      }
    };
    const isProjectStarted = () => {
      if (!!project?.broadcastStartedUtc) {
        setIsProjectStarted(true);
      }
    };
    isProjectBroadcastFinished();
    isProjectStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    const checkIfStartDateHasPassed = () => {
      if (
        !isReadOnly &&
        project?.scheduledDateTime &&
        moment.duration(getDateDifferenceFromNow(project?.scheduledDateTime)).asMinutes() < 1
      ) {
        setIsProjectStartDatePassed(true);
      }
    };
    const interval = setInterval(() => checkIfStartDateHasPassed(), 30000);
    checkIfStartDateHasPassed();
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    createDestinationDots();
  }, [project, createDestinationDots]);

useEffect(() => {
    createGlossaryDots();
  }, [project, createGlossaryDots]);

  return (
    <Accordion className="control-panel-accordion" collapsible={true} defaultIndex={0}>
      {/* Stream Source */}
      <AccordionItem className="control-panel-accordion-item">
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.stream-source.heading')}
          </AccordionButton>
        </h3>
        <AccordionPanel className="control-panel-accordion-panel">
          <StreamSourcePanel
            sourceKey={project?.stream?.streamKey || ''}
            sourceUrl={project?.stream?.streamUrl || ''}
          />
        </AccordionPanel>
      </AccordionItem>

      {/* Stream Destinations */}
      <AccordionItem>
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.stream-destinations.heading')}
            <div className="control-panel-accordion-button-arrow-multidots">{destinationDots}</div>
          </AccordionButton>
        </h3>
        <AccordionPanel className="control-panel-accordion-panel">
          <StreamDestinationsPanel
            onUpdate={() => {
              createDestinationDots();
            }}
          />
        </AccordionPanel>
      </AccordionItem>

      {/* Captioning */}
      <AccordionItem>
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.captioning.heading')}
          </AccordionButton>
          <div className="control-panel-accordion-button-on-off">
            <button
              className="control-panel-accordion-button-toggle-button"
              onClick={(event) => {
                event.stopPropagation();

                setCaptionMode(project?.encoder?.captionMode);

                if (project?.encoder?.captionMode === CaptionModeEnum.None) {
                  updateStream({
                    captionMode: captionMode,
                  });
                } else {
                  updateStream({
                    captionMode: CaptionModeEnum.None,
                  });
                }

                sendProjectToAllUsers(project as Project);
              }}
            >
              <span className="control-panel-accordion-button-on-off-button-back"></span>
              {project?.encoder && project?.encoder.captionMode !== CaptionModeEnum.None ? (
                <>
                  <span className="control-panel-accordion-button-on-off-text">
                    {t('labels.on')}
                  </span>
                  <span className="control-panel-accordion-button-on-off-button-switch"></span>
                </>
              ) : (
                <>
                  <span className="control-panel-accordion-button-on-off-text">
                    {t('labels.off')}
                  </span>
                  <span className="control-panel-accordion-button-on-off-button-switch control-panel-accordion-button-on-off-button-switch-off"></span>{' '}
                </>
              )}
            </button>
          </div>
        </h3>
        {project?.encoder?.captionMode !== CaptionModeEnum.None && (
          <>
            <AccordionPanel className="control-panel-accordion-panel">
              <CaptioningPanel projectUsers={project?.projectUsers || []} isReadOnly={isReadOnly} />
            </AccordionPanel>
          </>
        )}
      </AccordionItem>

      {/* Connector */}
      <AccordionItem>
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.connector.heading')}
          </AccordionButton>
        </h3>
        <AccordionPanel className="control-panel-accordion-panel">
          <ConnectorPanel projectUsers={project?.projectUsers || []} isReadOnly={isReadOnly} />
        </AccordionPanel>
      </AccordionItem>

      {project?.encoder?.captionMode !== CaptionModeEnum.None && (
        <>
          {/* Glossaries */}
          <AccordionItem>
            <h3>
              <AccordionButton className="control-panel-accordion-button">
                <img
                  src={ExpandCollapseIcon}
                  alt="open icon"
                  className="control-panel-accordion-button-arrow"
                />
                {t('producer.control-panel.glossaries.heading')}
                <div className="control-panel-accordion-button-arrow-multidots">{glossaryDots}</div>
              </AccordionButton>
            </h3>
            <AccordionPanel className="control-panel-accordion-panel">
              <GlossariesPanel
                onUpdate={() => {
                  createGlossaryDots();
                }}
              />
            </AccordionPanel>
          </AccordionItem>
          {/* Aggregator */}
          <AccordionItem>
            <h3>
              <AccordionButton className="control-panel-accordion-button">
                <img
                  src={ExpandCollapseIcon}
                  alt="open icon"
                  className="control-panel-accordion-button-arrow"
                />
                {t('producer.control-panel.aggregator.heading')}
              </AccordionButton>
            </h3>
            <AccordionPanel className="control-panel-accordion-panel">
              <AggregatorPanel />
            </AccordionPanel>
          </AccordionItem>

          {project?.encoder?.captionMode === CaptionModeEnum.Open && (
            <>
              {/* Overlay */}
              <AccordionItem>
                <h3>
                  <AccordionButton className="control-panel-accordion-button">
                    <img
                      src={ExpandCollapseIcon}
                      alt="open icon"
                      className="control-panel-accordion-button-arrow"
                    />
                    {t('producer.control-panel.overlay.heading')}
                  </AccordionButton>
                </h3>
                <AccordionPanel className="control-panel-accordion-panel">
                  <OverlayPanel />
                </AccordionPanel>
              </AccordionItem>
            </>
          )}
        </>
      )}

      {/* Transcoder */}
      <AccordionItem>
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.transcoder.heading')}
          </AccordionButton>
        </h3>
        <AccordionPanel className="control-panel-accordion-panel">
          <TranscoderPanel />
        </AccordionPanel>
      </AccordionItem>

      {/* Chat */}
      <AccordionItem>
        <h3>
          <AccordionButton className="control-panel-accordion-button">
            <img
              src={ExpandCollapseIcon}
              alt="open icon"
              className="control-panel-accordion-button-arrow"
            />
            {t('producer.control-panel.chat.heading')}
            {/* <img src={ChatPanelIcon} alt="chat panel" className="control-panel-chat-panel-icon" /> */}
          </AccordionButton>
        </h3>
        <AccordionPanel className="control-panel-accordion-panel">
          <Chat
            chatMessages={chatMessages}
            userId={`Producer-${project?.id}`}
            sendMessageToRoomCallback={sendMessageToRoomCallback}
            isChatConnected={isChatConnected}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default ProducerControlPanel;
